import { useImperativeHandle, useRef, forwardRef, useState, useMemo, createContext, useContext, useEffect } from "react";

/** Lib Imports */
import { Button } from "primereact/button";
import { InputNumber } from "primereact/inputnumber";
import { Checkbox } from "primereact/checkbox";
import Toast from "@moltech/molui/toast";
import MolGrid, { Column } from "@moltech/molui/molgrid";

/** Utilities */
import { encryptUrlData } from "../../../utils/encryption/CryptoService";
import { hasFunctionalPermission } from "../../../utils/authentication/authorizeNavigation";
import { SwalService } from "../../../utils/common/SwalService";
import { formatPhoneNumber } from "../../../utils/common/phoneFormat";

/** Components */
import Badges from "../../../components/common/badges/Badges";
import NoRecordFound from "../../../components/common/no-record-found/NoRecordFound";

/** ENUM */
import { ApplicantStatus } from "../../../enums/ApplicantStatus.ENUM";
import { securityKeys } from "../../../enums/SecurityKeys.ENUM";

/** RTK Query */
import {
  useDeleteDataMutation,
  useLazyGetGridDataQuery,
} from "../../../app/services/commonApi";

import '../Applicants.scss';
import { useReprocessApplicantResumeMutation } from '../../../app/services/automationApi';

/**
 * SelectionContext — shared between the grid body cells and the header checkbox.
 * Using Context means RowCheckbox and HeaderCheckbox components re-render whenever
 * selectedApplicantIds changes, independent of whether MolGrid re-calls body functions.
 */
const SelectionContext = createContext(null);

/** Row-level checkbox — consumes context so it always reflects current selection state */
const RowCheckbox = ({ applicantId }) => {
  const { selectedApplicantIds, setSelectedApplicantIds } = useContext(SelectionContext);
  return (
    <Checkbox
      checked={selectedApplicantIds.includes(applicantId)}
      onChange={(e) => {
        if (e.target.checked) {
          setSelectedApplicantIds((prev) => [...prev, applicantId]);
        } else {
          setSelectedApplicantIds((prev) => prev.filter((id) => id !== applicantId));
        }
      }}
    />
  );
};

/** Header (select-all) checkbox — reads current-page IDs from context */
const HeaderCheckbox = () => {
  const { selectedApplicantIds, setSelectedApplicantIds, pageApplicantIds } = useContext(SelectionContext);
  const allSelected = pageApplicantIds.length > 0 && pageApplicantIds.every((id) => selectedApplicantIds.includes(id));
  const someSelected = pageApplicantIds.some((id) => selectedApplicantIds.includes(id));

  return (
    <Checkbox
      checked={allSelected}
      indeterminate={someSelected && !allSelected}
      onChange={(e) => {
        if (e.target.checked) {
          setSelectedApplicantIds((prev) => [...new Set([...prev, ...pageApplicantIds])]);
        } else {
          setSelectedApplicantIds((prev) => prev.filter((id) => !pageApplicantIds.includes(id)));
        }
      }}
    />
  );
};

const ActionButtons = ({
  row,
  hasEditPermission,
  hasDeletePermission,
  onReprocessResume,
  onEdit,
  onDelete,
  reprocessingResumeId,
  isReprocessingResume,
}) => {

  const isReprocessing = reprocessingResumeId === row.applicantResumeId;
  const isAnyResumeReprocessing = isReprocessingResume || reprocessingResumeId !== null;

  const handleModifyDetails = (row) => {
    const applicantResumeId = row.applicantResumeId;
    window.open(
      `/modify-applicant-details/${encryptUrlData(applicantResumeId)}?from=applicants`,
      "_blank"
    );
  };

  return (
    <>
      {(hasEditPermission?.isEditable || hasEditPermission?.isViewOnly) &&
        (row.applicantStatusId === ApplicantStatus.DRAFT ? (
          <>
            <Button
              icon="pi pi-check-square"
              className="p-button-text"
              onClick={() => handleModifyDetails(row)}
              tooltip="Verify Candidate"
              data-pr-position="bottom"
            />
            <Button
              icon="pi pi-replay"
              className="p-button-text "
              onClick={() => onReprocessResume(row)}
              tooltip="Re-process Resume"
              data-pr-position="bottom"
              loading={isReprocessing}
              disabled={isAnyResumeReprocessing}
            />
          </>
        ) : (
          <Button
            icon="pi pi-pencil"
            className="p-button-text"
            onClick={() => onEdit(row)}
            tooltip="Edit"
            data-pr-position="bottom"
          />
        ))}

      {hasDeletePermission?.hasAccess && (
        <Button
          icon="pi pi-trash"
          className="p-button-text"
          onClick={() => onDelete(row)}
          tooltip="Delete"
          data-pr-position="bottom"
        />
      )}
    </>
  );
};

const renderStatus = (row) => (
  <Badges
    label={row.applicantStatusName}
    type={row.applicantStatusName}
    showDot={false}
  />
);

const JumpToPageInputTemplate = (options) => {
  const page = options.value;
  const totalPages = options.props.totalPages;
  const onChange = options.onChange;
  return (
    <span className="flex align-items-center gap-1 jump-to-page-container" style={{ whiteSpace: "nowrap" }}>
      <span className="text-sm text-color-secondary jump-label">Go To Page </span>
      <InputNumber
        value={page}
        onChange={(e) => onChange(e.value)}
        className="p-paginator-page-input"
        min={1}
        max={totalPages}
        disabled={options.disabled}
      />
    </span>
  );
};

const AppliedSinceBody = (row) => {
  if (!row.applyDate) return "";

  const appliedDate = new Date(row.applyDate + "Z");
  const now = new Date();

  const appliedMidnight = new Date(
    appliedDate.getFullYear(),
    appliedDate.getMonth(),
    appliedDate.getDate()
  );

  const todayMidnight = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate()
  );

  const diffInMs = todayMidnight - appliedMidnight;
  const days = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
  const months = Math.floor(days / 30);
  const years = Math.floor(days / 365);

  switch (true) {
    case days === 0:
      return "Today";
    case days === 1:
      return "1 day ago";
    case days < 30:
      return `${days} days ago`;
    case months === 1:
      return "1 month ago";
    case months < 12:
      return `${months} months ago`;
    case years === 1:
      return "1 year ago";
    default:
      return `${years} years ago`;
  }
};

const ApplicantList = forwardRef(
  ({ activeTab, statusId, filters, searchApplicant, onEdit, isBulkActionMode, selectedApplicantIds, setSelectedApplicantIds }, ref) => {
    const gridRef = useRef();
    const { confirm } = SwalService;

    const [applicantList, setApplicantList] = useState([]);
    const [reprocessingResumeId, setReprocessingResumeId] = useState(null);

    const [deleteApplicant] = useDeleteDataMutation();
    const [reprocessApplicantResume, { isLoading: isReprocessingResume }] = useReprocessApplicantResumeMutation();

    const hasEditPermission = hasFunctionalPermission(securityKeys.UPDATE_APPLICANT);
    const hasDeletePermission = hasFunctionalPermission(securityKeys.DELETE_APPLICANT);

    useEffect(() => {
      setApplicantList(null);
    }, [searchApplicant, filters, statusId]);

    useImperativeHandle(ref, () => ({
      reload: () => {
        if (gridRef.current) {
          gridRef.current.reload();
        }
      },
    }));

    const handleReprocessResume = async (rowData) => {
      if (!rowData || isReprocessingResume) return;

      setReprocessingResumeId(rowData.applicantResumeId);

      try {
        const result = await reprocessApplicantResume({
          applicantResumeBatchId: Number(rowData.applicantResumeBatchId),
          applicantResumeId: Number(rowData.applicantResumeId),
        }).unwrap();

        if (result?.success === false) {
          Toast.error(result?.message || "Failed to trigger resume reprocess.");
          return;
        }

        Toast.success(result?.message || "Resume reprocess has been triggered successfully.");
        gridRef.current?.reload();
      } catch (error) {
        Toast.error(error?.message || "Failed to trigger resume reprocess.");
      } finally {
        setReprocessingResumeId(null);
      }
    };

    const selectionContextValue = useMemo(() => ({
      selectedApplicantIds,
      setSelectedApplicantIds,
      pageApplicantIds: applicantList?.data?.map((a) => a.applicantId) ?? [],
    }), [selectedApplicantIds, setSelectedApplicantIds, applicantList]);

    const onCallback = (data) => {
      if (data) {
        if (gridRef.current) {
          gridRef.current.reload();
        }
        Toast.success(data.message);
      }
    };

    const formatDate = (date) => {
      if (!date) return null;
      const d = new Date(date);
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${d.getFullYear()}-${month}-${day}`;
    };

    const beforeGridLoad = (reqPrm) => {
      let applicantStatusIds = null;

      if (statusId && statusId !== 0) {
        applicantStatusIds = [statusId];
      } else if (filters?.applicantStatusId?.length) {
        applicantStatusIds = filters.applicantStatusId;
      }

      const payload = {
        pageIndex: Math.floor(reqPrm.first / reqPrm.rows) + 1,
        pageSize: reqPrm.rows,
        firstName: searchApplicant || "",
        lastName: "",
        positionAppliedIds: filters?.positionApplied?.length ? filters.positionApplied : null,
        sourceId: filters?.sourceId || null,
        applyDate: filters?.applyDate ? formatDate(filters.applyDate) : null,
        applicantStatusIds,
        hrId: filters?.hrId || null,
        SortField: reqPrm.sortField ?? "ApplicantId",
        SortOrder: reqPrm.sortOrder === 1 ? "ASC" : "DESC",
      };
      return payload;
    };

    const afterGridLoad = (res) => {
      const paginatedResult = res?.data;
      setApplicantList(paginatedResult);
      return {
        data: paginatedResult?.data?.map((item) => ({
          ...item,
          responsibleUser: Array.isArray(item.responsibleUser)
            ? item.responsibleUser.join(", ")
            : item.responsibleUser,
        })),
        count: paginatedResult?.count ?? 0,
      };
    };

    const handleApplicantDetails = (applicantData) => {
      if (applicantData) {
        let applicantId = applicantData.applicantId;
        if (applicantData.applicantStatusId === ApplicantStatus.DRAFT) {
          window.open(
            `/modify-applicant-details/${encryptUrlData(applicantId)}?from=applicants`,
            "_blank"
          );
        } else {
          window.open(`/applicant-details/${encryptUrlData(applicantId)}`, "_blank");
        }
      }
    };

    const onDelete = (rowData) => {
      confirm({ text: "Would you like to delete this Candidate?" }).then((confirmed) => {
        if (confirmed) {
          deleteApplicant({
            url: "/ManageApplicants/DeleteApplicant",
            data: rowData.applicantId,
          }).then((res) => {
            let message = res?.data?.message || "Candidate deleted successfully!";
            if (onCallback) {
              onCallback({ ...res.data, message });
            }
          });
        }
      });
    };

    const handleActionButtons = (row) => (
      <ActionButtons
        row={row}
        hasEditPermission={hasEditPermission}
        hasDeletePermission={hasDeletePermission}
        onEdit={handleApplicantDetails}
        onReprocessResume={handleReprocessResume}
        onDelete={onDelete}
        reprocessingResumeId={reprocessingResumeId}
        isReprocessingResume={isReprocessingResume}
      />
    );

    const getCandidateName = (row) => {
      const encryptedApplicantId = encryptUrlData(row.applicantId);
      const encryptedApplicantResumeId = encryptUrlData(row.applicantResumeId);
      const path =
        row.applicantStatusId === ApplicantStatus.DRAFT
          ? `/modify-applicant-details/${encryptedApplicantResumeId}?from=applicants`
          : `/applicant-details/${encryptedApplicantId}`;
      return (
        <a
          href={path}
          target="_blank"
          rel="noopener noreferrer"
          className="font-semibold hover:text-primary cursor-pointer"
          style={{ textDecoration: "underline" }}
          onClick={(e) => {
            e.preventDefault();
            window.open(path, "_blank");
          }}
        >
          {row.firstName} {row.lastName}
        </a>
      );
    };
    const renderRowCheckbox = (row) => {
      return row?.applicantId ? <RowCheckbox applicantId={row.applicantId} /> : null;
    };
    const getColumns = (activeTab) => {
      const columns = [];

      if (isBulkActionMode) {
        columns.push({
          field: "selection",
          header: <HeaderCheckbox />,
          body: renderRowCheckbox,
          style: { width: "4rem" },
        });
      }

      columns.push({
        field: "batchName",
        header: "Batch Name",
        body: (row) => row.batchName,
      });

      columns.push({
        field: "firstName",
        header: "Candidate Name",
        body: (row) => getCandidateName(row),
      });

      columns.push({ field: "applicantEmail", header: "Email" });
      columns.push({
        field: "applicantPhone",
        header: "Phone",
        body: (row) => formatPhoneNumber(row.applicantPhone),
      });

      columns.push({
        field: "applicantStatusName",
        header: "Current Status",
        body: renderStatus,
      });

      columns.push({ field: "positionName", header: "Job Opening" });
      columns.push({ field: "responsibleUserName", header: "HR Owner" });

      columns.push({
        field: "applyDate",
        header: "Applied On",
        body: (row) =>
          row.applyDate
            ? new Date(row.applyDate).toLocaleDateString("en-IN", {
              timeZone: "Asia/Kolkata",
            })
            : "",
      });

      if (statusId === ApplicantStatus.FOLLOW_UP) {
        columns.push({
          field: "followUpDateTime",
          header: "Follow-Up Date",
          body: (row) =>
            row.followUpDateTime
              ? new Date(row.followUpDateTime).toLocaleString("en-IN", {
                timeZone: "Asia/Kolkata",
              })
              : "",
        });
      }

      columns.push({
        field: "appliedSince",
        header: "Applied Since",
        body: (row) => AppliedSinceBody(row),
      });

      columns.push({ field: "sourceName", header: "Candidate Source" });

      columns.push({
        field: "actions",
        header: "Actions",
        style: { width: "5rem" },
        body: handleActionButtons,
        align: "center",
        frozen: true,
        alignFrozen: "right",
      });

      return columns;
    };

    return (
      <SelectionContext.Provider value={selectionContextValue}>
        <div className="applicant-list">
          {applicantList?.data?.length === 0 ? (
            <NoRecordFound message="No candidates found with selected status." />
          ) : (
            <MolGrid
              ref={gridRef}
              key={`applicant-grid-${statusId}`}
              url="/ManageApplicants/GetApplicantList"
              client={useLazyGetGridDataQuery}
              afterLoad={afterGridLoad}
              beforeLoad={beforeGridLoad}
              paginator
              rows={25}
              rowsPerPageOptions={[10, 25, 50, 100]}
              scrollable
              scrollHeight="flex"
              filterDisplay="none"
              params={{ statusId }}
              paginatorTemplate={{
                layout: "JumpToPageInput CurrentPageReport  RowsPerPageDropdown FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink",
                JumpToPageInput: JumpToPageInputTemplate,
              }}
              currentPageReportTemplate="{last} of {totalRecords}"
            >
              {getColumns(activeTab).map((col) => (
                <Column
                  key={col.field}
                  field={col.field}
                  header={col.header}
                  body={col.body}
                  style={col.style}
                  align={col.align}
                  alignFrozen={col.alignFrozen}
                  hideColumnMenu={true}
                  className={
                    col.field === "applicantStatusName" ? "center-grid-data" : ""
                  }
                  headerClassName={
                    col.field === "applicantStatusName" ? "center-grid-data" : ""
                  }
                  frozen={col.frozen}
                  sortable={col.field === "firstName"}
                />
              ))}
            </MolGrid>
          )}
        </div>
      </SelectionContext.Provider>
    );
  }
);

export default ApplicantList;
